import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Eye, EyeOff, ArrowLeft, ShieldCheck } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";

import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import { requestPasswordResetOtp, verifyPasswordResetOtp } from "@/lib/password-reset.functions";
import { getPendingTotpFactorId, verifyTotpCode } from "@/lib/mfa-helpers";
import logoImage from "@/assets/finntastic-logo-circle.png";


const ENABLE_EMAIL_PASSWORD = true;

type Mode = "login" | "forgot-email" | "forgot-otp" | "forgot-reset" | "forgot-success";

function LoginPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const requestReset = useServerFn(requestPasswordResetOtp);
  const verifyReset = useServerFn(verifyPasswordResetOtp);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<Mode>("login");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [cooldown, setCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  useEffect(() => {
    if (!loading && user && mode === "login" && !mfaFactorId) {
      navigate({ to: "/" });
    }
  }, [loading, user, navigate, mode, mfaFactorId]);

  async function handleSendOtp(e: React.FormEvent) {
    e.preventDefault();
    if (cooldown > 0) return;
    setError(null);
    setBusy(true);
    try {
      await requestReset({ data: { email } });
      const next = otpAttempts + 1;
      setOtpAttempts(next);
      setCooldown(next >= 3 ? 50 : 10);
      setMode("forgot-otp");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "failed to send code");
    } finally {
      setBusy(false);
    }
  }

  function handleContinueToReset(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(otp)) {
      setError("enter the 6-digit code");
      return;
    }
    setMode("forgot-reset");
  }

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError("passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      setError("password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await verifyReset({ data: { email, code: otp, password: newPassword } });
      setMode("forgot-success");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "failed to update password");
    } finally {
      setBusy(false);
    }
  }

  function backToLogin() {
    setMode("login");
    setError(null);
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      const pendingFactor = await getPendingTotpFactorId();
      if (pendingFactor) {
        setMfaFactorId(pendingFactor);
        return;
      }
      navigate({ to: "/" });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "sign in failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleMfaSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaFactorId) return;
    setError(null);
    if (!/^\d{6}$/.test(mfaCode.trim())) {
      setError("enter the 6-digit code from your authenticator app");
      return;
    }
    setBusy(true);
    try {
      await verifyTotpCode(mfaFactorId, mfaCode.trim());
      setMfaFactorId(null);
      setMfaCode("");
      navigate({ to: "/" });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "invalid code, try again");
    } finally {
      setBusy(false);
    }
  }

  async function handleMfaCancel() {
    await supabase.auth.signOut();
    setMfaFactorId(null);
    setMfaCode("");
    setError(null);
  }

  async function handleGoogle() {
    setError(null);
    setBusy(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}/onboarding`,
        extraParams: { prompt: "select_account" },
      });
      if (result.error) throw result.error;
      if (result.redirected) return;
      navigate({ to: "/onboarding" });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "google sign-in failed");
      setBusy(false);
    }
  }

  const title =
    mode === "login" ? "Welcome back"
    : mode === "forgot-email" ? "Forgot password"
    : mode === "forgot-otp" ? "Enter code"
    : mode === "forgot-reset" ? "New password"
    : "Password changed";

  const subtitle =
    mode === "login" ? "Sign in to save and personalise your finds across Finland."
    : mode === "forgot-email" ? "Enter your email and we'll send you a 6-digit code."
    : mode === "forgot-otp" ? `We sent a 6-digit code to ${email}.`
    : mode === "forgot-reset" ? "Choose a strong password you'll remember."
    : "Your password has been updated successfully.";

  return (
    <div className="min-h-screen grid bg-background">
      {/* Form panel */}
      <div className="flex flex-col px-6 py-10 sm:px-12 lg:px-16 lg:py-16">
        <div className="flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="size-4" /> Back to site
          </Link>
          <div className="flex items-center gap-2">
            <img src={logoImage} alt="" className="size-7 rounded-full" />
            <span className="text-sm font-semibold">what Finland</span>
          </div>
        </div>


        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md py-10">
            <div className="mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
              <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
            </div>

            {mode === "login" && mfaFactorId && (
              <form onSubmit={handleMfaSubmit} className="space-y-5">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3.5 text-sm text-emerald-900 flex gap-2">
                  <ShieldCheck className="size-4 mt-0.5 shrink-0" />
                  <span>Two-factor authentication required. Open your authenticator app and enter the 6-digit code.</span>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mfa-code">Authentication code</Label>
                  <Input
                    id="mfa-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                    required
                    autoFocus
                    placeholder="123456"
                    className="text-center tracking-[0.5em] font-mono text-lg h-12"
                  />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button type="submit" disabled={busy} className="w-full h-11">
                  {busy ? "Verifying…" : "Verify and continue"}
                </Button>
                <button
                  type="button"
                  onClick={handleMfaCancel}
                  className="block w-full text-center text-xs text-muted-foreground hover:text-foreground"
                >
                  Cancel and sign out
                </button>
              </form>
            )}

            {mode === "login" && !mfaFactorId && (
              <>
                {ENABLE_EMAIL_PASSWORD ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGoogle}
                      disabled={busy}
                      className="w-full h-11 gap-2 mb-4"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
                      </svg>
                      Continue with Google
                    </Button>
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">or email</span>
                      </div>
                    </div>


                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          autoComplete="email"
                          placeholder="you@example.com"
                          className="h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password">Password</Label>
                          <button
                            type="button"
                            onClick={() => { setError(null); setMode("forgot-email"); }}
                            className="text-xs font-medium text-primary hover:underline"
                          >
                            Forgot password?
                          </button>
                        </div>
                        <div className="relative">
                          <Input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={8}
                            autoComplete="current-password"
                            placeholder="••••••••"
                            className="pr-10 h-11"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            aria-label={showPassword ? "hide password" : "show password"}
                            tabIndex={-1}
                          >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                      </div>

                      {error && (
                        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                          {error}
                        </div>
                      )}

                      <Button type="submit" disabled={busy} className="w-full h-11 font-semibold">
                        {busy ? "Signing in…" : "Sign in"}
                      </Button>
                    </form>
                  </>
                ) : (
                  error && (
                    <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                      {error}
                    </div>
                  )
                )}

                <p className="mt-8 text-sm text-center text-muted-foreground">
                  New here?{" "}
                  <Link to="/register" className="font-medium text-primary hover:underline">
                    Create an account
                  </Link>
                </p>
              </>
            )}

            {mode === "forgot-email" && (
              <form onSubmit={handleSendOtp} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="h-11"
                  />
                </div>
                {error && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}
                <Button type="submit" disabled={busy || cooldown > 0} className="w-full h-11">
                  {busy ? "Sending…" : cooldown > 0 ? `Wait ${cooldown}s` : "Send code"}
                </Button>
                <button type="button" onClick={backToLogin} className="w-full text-sm text-muted-foreground hover:text-foreground">
                  ← Back to sign in
                </button>
              </form>
            )}

            {mode === "forgot-otp" && (
              <form onSubmit={handleContinueToReset} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="otp">6-digit code</Label>
                  <Input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    required
                    maxLength={6}
                    autoComplete="one-time-code"
                    placeholder="123456"
                    className="h-12 text-center tracking-[0.5em] font-mono text-lg"
                  />
                </div>
                {error && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}
                <Button type="submit" disabled={otp.length < 6} className="w-full h-11">
                  Continue
                </Button>
                <button
                  type="button"
                  onClick={() => setMode("forgot-email")}
                  className="w-full text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
                  disabled={cooldown > 0}
                >
                  {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
                </button>
              </form>
            )}

            {mode === "forgot-reset" && (
              <form onSubmit={handleResetPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      className="pr-10 h-11"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showNewPassword ? "hide password" : "show password"}
                      tabIndex={-1}
                    >
                      {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      className="pr-10 h-11"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showConfirmPassword ? "hide password" : "show password"}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}
                <Button type="submit" disabled={busy} className="w-full h-11">
                  {busy ? "Updating…" : "Change password"}
                </Button>
              </form>
            )}

            {mode === "forgot-success" && (
              <div className="space-y-5">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800 flex gap-2">
                  <ShieldCheck className="size-4 mt-0.5 shrink-0" />
                  <span>Password changed successfully. You can now sign in with your new password.</span>
                </div>
                <Button type="button" onClick={backToLogin} className="w-full h-11">
                  Back to sign in
                </Button>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          By continuing you agree to our{" "}
          <Link to="/" className="underline hover:text-foreground">Terms</Link> and{" "}
          <Link to="/" className="underline hover:text-foreground">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Sign in — what Finland" },
      { name: "description", content: "Sign in to your what Finland account." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LoginPage,
});
